package com.eladfinish.bible.contest.ui.components.toolbar.autohide

import androidx.compose.foundation.ScrollState
import androidx.compose.foundation.gestures.awaitEachGesture
import androidx.compose.foundation.gestures.awaitFirstDown
import androidx.compose.foundation.lazy.LazyListState
import androidx.compose.foundation.pager.PagerState
import androidx.compose.runtime.Composable
import androidx.compose.runtime.DisposableEffect
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.compositionLocalOf
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import androidx.compose.runtime.snapshotFlow
import androidx.compose.ui.Modifier
import androidx.compose.ui.geometry.Offset
import androidx.compose.ui.input.nestedscroll.NestedScrollConnection
import androidx.compose.ui.input.nestedscroll.NestedScrollSource
import androidx.compose.ui.input.nestedscroll.nestedScroll
import androidx.compose.ui.input.pointer.PointerEventPass
import androidx.compose.ui.input.pointer.pointerInput
import androidx.compose.ui.platform.LocalDensity
import androidx.compose.ui.unit.Velocity
import androidx.compose.ui.unit.dp
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.Job
import kotlinx.coroutines.delay
import kotlinx.coroutines.flow.distinctUntilChanged
import kotlinx.coroutines.flow.drop
import kotlinx.coroutines.launch
import org.koin.compose.koinInject
import kotlin.time.TimeSource

private val TriggerReference = 30.dp

/** A mouse wheel never lifts: this long without a scroll ends its scroll. */
private const val IDLE_END_OF_SCROLL_MS = 150L

/** `null` when this screen's toolbar does not hide, or the setting is off. */
internal val LocalToolbarAutoHideController = compositionLocalOf<ToolbarAutoHideController?> { null }

/**
 * Feeds the user's scrolling to [ToolbarAutoHideLogic]. Only the user's: a nested scroll connection does not
 * hear what the app scrolls by itself (a bookmark, a verse, an aliyah), so those do not move the toolbar.
 */
internal class ToolbarAutoHideController(
    private val toolbarAutoHide: ToolbarAutoHide,
    triggerReferencePx: Float,
    private val scope: CoroutineScope,
) : NestedScrollConnection {

    private val logic = ToolbarAutoHideLogic(triggerReferencePx)
    private val startMark = TimeSource.Monotonic.markNow()
    private var isInScroll = false
    private var idleEndJob: Job? = null

    /** The active page's position; the page reports it, the controller reads it when it needs it. */
    var activeScrollMetrics: (() -> ToolbarAutoHideScrollMetrics)? by mutableStateOf(null)

    var isPressed = false

    private fun nowMs() = startMark.elapsedNow().inWholeMilliseconds

    override fun onPreScroll(available: Offset, source: NestedScrollSource): Offset {
        val metrics = activeScrollMetrics?.invoke()
        if (available.y != 0f && metrics != null) {
            logic.isHidden = toolbarAutoHide.transition.value.isHidden
            if (!isInScroll) {
                isInScroll = true
                logic.onScrollBegin(nowMs())
            }
            start(
                logic.onScrollUpdate(
                    delta = -available.y,
                    isInertial = source == NestedScrollSource.SideEffect,
                    nowMs = nowMs(),
                    metrics = metrics,
                    toolbarHeightPx = toolbarAutoHide.toolbarHeightPx,
                )
            )
            restartIdleEnd()
        }
        return Offset.Zero
    }

    override suspend fun onPostFling(consumed: Velocity, available: Velocity): Velocity {
        endScroll()
        return Velocity.Zero
    }

    fun forceShow() {
        logic.isHidden = toolbarAutoHide.transition.value.isHidden
        start(logic.forceShow(nowMs()))
    }

    private fun restartIdleEnd() {
        idleEndJob?.cancel()
        idleEndJob = scope.launch {
            delay(IDLE_END_OF_SCROLL_MS)
            if (!isPressed) endScroll()
        }
    }

    private fun endScroll() {
        idleEndJob?.cancel()
        if (!isInScroll) return
        isInScroll = false
        val metrics = activeScrollMetrics?.invoke() ?: return
        logic.isHidden = toolbarAutoHide.transition.value.isHidden
        start(
            logic.onScrollEnd(
                nowMs = nowMs(),
                metrics = metrics,
                toolbarHeightPx = toolbarAutoHide.toolbarHeightPx,
            )
        )
    }

    private fun start(transition: ToolbarAutoHideTransition?) {
        transition?.let(toolbarAutoHide::start)
    }
}

/**
 * For a screen with chapters in a pager: a new page starts with the toolbar shown. So does a page at its top
 * or with nothing to scroll (the text may shrink to fit after a rotation or a new text size), since no scroll
 * could bring the toolbar back there.
 */
@Composable
internal fun rememberToolbarAutoHideController(
    pagerState: PagerState,
    toolbarAutoHide: ToolbarAutoHide = koinInject(),
): ToolbarAutoHideController? {
    if (!remember { toolbarAutoHide.isEnabled() }) return null

    val triggerReferencePx = with(LocalDensity.current) { TriggerReference.toPx() }
    val scope = rememberCoroutineScope()
    val controller = remember {
        ToolbarAutoHideController(
            toolbarAutoHide = toolbarAutoHide,
            triggerReferencePx = triggerReferencePx,
            scope = scope,
        )
    }

    LaunchedEffect(pagerState, controller) {
        snapshotFlow { pagerState.currentPage }
            .drop(1)
            .collect { controller.forceShow() }
    }

    LaunchedEffect(controller) {
        snapshotFlow {
            val metrics = controller.activeScrollMetrics?.invoke()
            metrics != null && (metrics.offset <= 0f || metrics.maxOffset <= 0f)
        }
            .distinctUntilChanged()
            .collect { hasNoScrollBack -> if (hasNoScrollBack) controller.forceShow() }
    }

    DisposableEffect(controller) {
        controller.forceShow()
        onDispose { controller.forceShow() }
    }

    return controller
}

internal fun Modifier.toolbarAutoHide(controller: ToolbarAutoHideController?): Modifier {
    if (controller == null) return this
    return nestedScroll(controller).pointerInput(controller) {
        awaitEachGesture {
            awaitFirstDown(requireUnconsumed = false, pass = PointerEventPass.Initial)
            controller.isPressed = true
            do {
                val event = awaitPointerEvent(PointerEventPass.Initial)
            } while (event.changes.any { it.pressed })
            controller.isPressed = false
        }
    }
}

@Composable
internal fun ReportActiveScroll(isActivePage: Boolean, scrollState: ScrollState) {
    val controller = LocalToolbarAutoHideController.current ?: return
    LaunchedEffect(isActivePage, scrollState) {
        if (isActivePage) {
            controller.activeScrollMetrics = {
                ToolbarAutoHideScrollMetrics(
                    offset = scrollState.value.toFloat(),
                    maxOffset = scrollState.maxValue.toFloat(),
                )
            }
        }
    }
}

/** A lazy list knows its position only near its top; further down, what matters is whether it can scroll on. */
@Composable
internal fun ReportActiveScroll(isActivePage: Boolean, listState: LazyListState) {
    val controller = LocalToolbarAutoHideController.current ?: return
    LaunchedEffect(isActivePage, listState) {
        if (isActivePage) {
            controller.activeScrollMetrics = {
                val offset = if (listState.firstVisibleItemIndex == 0) {
                    listState.firstVisibleItemScrollOffset.toFloat()
                } else {
                    Float.MAX_VALUE / 2
                }
                ToolbarAutoHideScrollMetrics(
                    offset = offset,
                    maxOffset = if (listState.canScrollForward) Float.MAX_VALUE else offset,
                )
            }
        }
    }
}
